import { useEffect, useState } from 'react'
import { Table } from 'react-bootstrap'
import { Link, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import { KTSVG } from '../../_metronic/helpers'
import { getRequestOptions, postRequestOptions } from '../helpers/Fetchwrapper'
import validationErrorMsg from '../helpers/ValidationErrorMsg'

interface InterBuLine {
	idSalesInvoiceItems: number
	idSalesOrderRows: number
	productName: string
	packSize: string
	quantity: number
	unitName: string
	rate: number
	amount: number
	interBuQty: string
	interBuUnitRate: string
	vatPercent: string
}

interface SavedLine {
	interBuRate: number
	vatPercent: number
}

// The VAT (%) last saved, offered for the lines that have none saved yet.
const VAT_PERCENT_KEY = 'interBuVatPercent'

const storedVatPercent = () => {
	try {
		return localStorage.getItem(VAT_PERCENT_KEY) ?? ''
	} catch (error) {
		return ''
	}
}

const storeVatPercent = (value: string) => {
	try {
		localStorage.setItem(VAT_PERCENT_KEY, value)
	} catch (error) {
		// Not kept: the next invoice starts with an empty VAT (%).
	}
}

const numberFormat = (value: number) => {
	return value.toLocaleString('en-US', {
		style: 'decimal',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})
}

const roundTo = (value: number, places: number) => Math.round(value * 10 ** places) / 10 ** places

// A saved decimal ("24.000") as the input shows it ("24"), or empty when nothing is saved.
const savedInput = (value: any) => (value === null || value === undefined ? '' : String(parseFloat(value)))

function SalesInvoiceInterBu() {
	const { idSalesInvoice }: { idSalesInvoice: string } = useParams()
	const [lines, setLines] = useState<InterBuLine[]>([])
	const [invoiceNo, setInvoiceNo] = useState('')
	const [clientName, setClientName] = useState('')
	const [loading, setLoading] = useState(true)
	const [saving, setSaving] = useState(false)

	useEffect(() => {
		setLoading(true)
		fetch(process.env.REACT_APP_API_URL + 'sales-invoice/' + idSalesInvoice, getRequestOptions())
			.then((resp) => resp.json())
			.then((resp) => {
				const items = resp.data?.items || []
				const salesOrder = resp.data?.sales_order || resp.data?.salesOrder || {}
				const defaultVatPercent = storedVatPercent()
				setInvoiceNo(resp.data?.invoiceId || '')
				setClientName(salesOrder.company?.company_name || '')
				setLines(items.map((item: any) => {
					const orderRow = item.sales_order_row || item.salesOrderRow || {}
					const product = orderRow.product || {}
					const packUnit = product.item?.unit?.unit_name || ''

					return {
						idSalesInvoiceItems: item.id_sales_invoice_items,
						idSalesOrderRows: orderRow.id_sales_order_rows || orderRow.idSalesOrderRows,
						productName: orderRow.alternate_name?.alternate_name
							|| orderRow.alternateName?.alternate_name
							|| product.product_name
							|| product.productName
							|| '',
						packSize: `${product.packing || ''} ${packUnit}`.trim(),
						quantity: parseFloat(item.delivered_quantity ?? item.deliveredQuantity ?? 0),
						unitName: product.unit?.unit_name || product.unit?.unitName || '',
						rate: parseFloat(orderRow.unit_rate ?? orderRow.unitRate ?? 0),
						amount: parseFloat(item.delivered_amount ?? item.deliveredAmount ?? 0),
						interBuQty: savedInput(item.inter_bu_qty),
						interBuUnitRate: savedInput(item.inter_bu_unit_rate),
						vatPercent: item.inter_bu_vat_percent === null || item.inter_bu_vat_percent === undefined
							? defaultVatPercent
							: savedInput(item.inter_bu_vat_percent),
					}
				}).filter((line: InterBuLine) => line.quantity > 0))
				setLoading(false)
			})
			.catch(() => {
				setLines([])
				setLoading(false)
			})
	}, [idSalesInvoice])

	// Inter BU Rate = (Inter BU Qty x Inter BU Unit Rate) / Quantity, rounded as the server saves it; null until both are entered.
	const interBuRate = (line: InterBuLine) => {
		const qty = parseFloat(line.interBuQty)
		const unitRate = parseFloat(line.interBuUnitRate)
		if (line.interBuQty === '' || line.interBuUnitRate === '' || isNaN(qty) || isNaN(unitRate) || qty < 0 || unitRate < 0) {
			return null
		}
		return roundTo(roundTo(qty, 3) * roundTo(unitRate, 2) / line.quantity, 2)
	}

	const isValidRate = (line: InterBuLine) => {
		const rate = interBuRate(line)
		return rate !== null && rate <= line.rate
	}

	const interBuAmount = (line: InterBuLine) => {
		const rate = interBuRate(line)
		if (rate === null || !isValidRate(line)) {
			return null
		}
		return Math.min(roundTo(rate * line.quantity, 2), line.amount)
	}

	// VAT (%) may be left empty (no VAT), else 0 to 100.
	const isValidVat = (line: InterBuLine) => {
		if (line.vatPercent === '') {
			return true
		}
		const percent = parseFloat(line.vatPercent)
		return !isNaN(percent) && percent >= 0 && percent <= 100
	}

	// VAT Amount = Inter BU amount x VAT (%), rounded as the server saves it.
	const vatAmount = (line: InterBuLine) => {
		const amount = interBuAmount(line)
		if (amount === null || !isValidVat(line)) {
			return null
		}
		return roundTo(amount * roundTo(parseFloat(line.vatPercent) || 0, 2) / 100, 2)
	}

	// Invoice 1 Amount = Inter BU amount + VAT Amount.
	const invoice1Amount = (line: InterBuLine) => {
		const amount = interBuAmount(line)
		const vat = vatAmount(line)
		return amount === null || vat === null ? null : roundTo(amount + vat, 2)
	}

	// Invoice 2 Amount = Amount - Invoice 1 Amount.
	const invoice2Amount = (line: InterBuLine) => {
		const invoice1 = invoice1Amount(line)
		return invoice1 === null ? null : roundTo(line.amount - invoice1, 2)
	}

	const canPrint = lines.length > 0 && lines.every((line) => isValidRate(line) && isValidVat(line))

	const totalAmount = lines.reduce((sum, line) => sum + line.amount, 0)
	const vatTotal = lines.reduce((sum, line) => sum + (vatAmount(line) ?? 0), 0)
	const invoice1Total = lines.reduce((sum, line) => sum + (invoice1Amount(line) ?? 0), 0)
	const invoice2Total = lines.reduce((sum, line) => sum + (invoice2Amount(line) ?? 0), 0)

	const handleLineChange = (index: number, field: 'interBuQty' | 'interBuUnitRate' | 'vatPercent', value: string) => {
		setLines((prev) => {
			const updated = [...prev]
			updated[index] = { ...updated[index], [field]: value }
			return updated
		})
	}

	// Saves every line and answers with the saved Inter BU Rate and VAT (%) of each sales order row, or null when it failed.
	const saveRates = async (): Promise<Record<number, SavedLine> | null> => {
		setSaving(true)
		try {
			const response = await fetch(
				`${process.env.REACT_APP_API_URL}sales-invoice/inter-bu-rates`,
				postRequestOptions({
					idSalesInvoice,
					rows: lines.map((line) => ({
						idSalesInvoiceItems: line.idSalesInvoiceItems,
						interBuQty: line.interBuQty,
						interBuUnitRate: line.interBuUnitRate,
						vatPercent: line.vatPercent,
					})),
				})
			)
			const respData = await response.json()
			if (respData.success !== true) {
				Swal.fire({
					icon: 'error',
					title: 'Error',
					html: validationErrorMsg(respData.errors ?? respData.errorMessage ?? respData.message).join('<br>'),
				})
				return null
			}

			const savedLines: Record<number, SavedLine> = {}
			respData.data.forEach((row: any) => {
				savedLines[row.idSalesOrderRows] = { interBuRate: row.interBuRate, vatPercent: row.vatPercent }
			})

			const enteredVat = lines.find((line) => line.vatPercent !== '')
			if (enteredVat) {
				storeVatPercent(enteredVat.vatPercent)
			}
			return savedLines
		} catch (error) {
			console.log(error, 'catch the hoop')
			Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to save the Inter BU rates.' })
			return null
		} finally {
			setSaving(false)
		}
	}

	const handleSave = async () => {
		if (!canPrint) {
			return
		}
		if (await saveRates()) {
			Swal.fire({ icon: 'success', title: 'Saved', text: 'Inter BU rates saved.' })
		}
	}

	const handlePrint = async (part: 'entered' | 'remaining') => {
		if (!canPrint) {
			return
		}

		// Open the tab before saving: one opened after waiting for the save is blocked as a popup.
		const printWindow = window.open('', '_blank')
		const savedLines = await saveRates()
		if (!savedLines) {
			printWindow?.close()
			return
		}

		const params = new URLSearchParams()
		lines.forEach((line) => {
			params.append(`interBuRates[${line.idSalesOrderRows}]`, String(savedLines[line.idSalesOrderRows].interBuRate))
			params.append(`interBuVat[${line.idSalesOrderRows}]`, String(savedLines[line.idSalesOrderRows].vatPercent))
		})
		params.append('interBuPart', part)
		const url = `${process.env.REACT_APP_BACKEND}invoiceDownload/${idSalesInvoice}?${params.toString()}`

		if (printWindow) {
			printWindow.opener = null
			printWindow.location.href = url
		} else {
			window.open(url, '_blank', 'noopener,noreferrer')
		}
	}

	return (
		<div className='card card-custom'>
			<div className='card-header'>
				<div className="card-title">
					<KTSVG path='/media/svg/icons/Devices/Printer.svg' className='svg-icon-1 svg-icon-primary' />
					<span className='text fw-bolder' style={{ marginLeft: '10px' }}>
						Inter BU Invoice {invoiceNo && <>- {invoiceNo}</>}
					</span>
				</div>
				<div className="card-toolbar">
					<Link to='/sales-invoices' className="btn btn-sm btn-secondary">Back to Sales Invoices</Link>
				</div>
			</div>

			<div className='card-body'>
				{loading && <div>Loading...</div>}
				{!loading && (
					<>
						{clientName && <p className="mb-4"><b>Client:</b> {clientName}</p>}
						<p className="text-muted">
							Enter the Inter BU quantity and unit rate for each product: the Inter BU Rate is <b>(Inter BU Qty × Inter BU Unit Rate) ÷ Quantity</b>. Invoice 1 prints with the Inter BU Rate plus the VAT of the entered <b>VAT (%)</b> (Invoice 1 Amount = Inter BU amount + VAT Amount) and Invoice 2 prints the total remaining amount (Invoice 2 Amount = Amount - Invoice 1 Amount) as a single <b>Transportation and loading unloading cost</b> item. The quantities, rates and VAT are saved when you save or print; the VAT (%) you save is offered for the next invoice.
						</p>
						<Table size="sm" bordered hover responsive>
							<thead>
								<tr>
									<th>SN</th>
									<th>Product</th>
									<th>Pack Size</th>
									<th className="text-end">Quantity</th>
									<th>Unit</th>
									<th className="text-end">Rate</th>
									<th className="text-end">Amount</th>
									<th className="text-end">Inter BU Qty</th>
									<th className="text-end">Inter BU Unit Rate</th>
									<th className="text-end">Inter BU Rate</th>
									<th className="text-end">VAT (%)</th>
									<th className="text-end">VAT Amount</th>
									<th className="text-end">Invoice 1 Amount</th>
									<th className="text-end">Remaining Rate</th>
									<th className="text-end">Invoice 2 Amount</th>
								</tr>
							</thead>
							<tbody>
								{lines.map((line, index) => {
									const amount = interBuAmount(line)
									const rate = interBuRate(line)
									const isEntered = line.interBuQty !== '' && line.interBuUnitRate !== ''
									const hasError = isEntered && !isValidRate(line)
									const vat = vatAmount(line)
									const invoice1 = invoice1Amount(line)
									const invoice2 = invoice2Amount(line)
									return (
										<tr key={line.idSalesOrderRows || index}>
											<td>{index + 1}</td>
											<td>{line.productName}</td>
											<td>{line.packSize}</td>
											<td className="text-end">{line.quantity}</td>
											<td>{line.unitName}</td>
											<td className="text-end">{numberFormat(line.rate)}</td>
											<td className="text-end">{numberFormat(line.amount)}</td>
											<td style={{ minWidth: '110px' }}>
												<input
													type="number"
													min={0}
													step="0.001"
													className="form-control form-control-sm text-end"
													value={line.interBuQty}
													onChange={(event) => handleLineChange(index, 'interBuQty', event.target.value)}
												/>
											</td>
											<td style={{ minWidth: '120px' }}>
												<input
													type="number"
													min={0}
													step="0.01"
													className="form-control form-control-sm text-end"
													value={line.interBuUnitRate}
													onChange={(event) => handleLineChange(index, 'interBuUnitRate', event.target.value)}
												/>
											</td>
											<td style={{ minWidth: '140px' }}>
												<input
													type="text"
													readOnly
													tabIndex={-1}
													className={`form-control form-control-sm form-control-solid text-end ${hasError ? 'is-invalid' : ''}`}
													value={rate === null ? '' : numberFormat(rate)}
												/>
												{hasError && (
													<div className="invalid-feedback">
														{rate === null ? 'Enter 0 or more' : `Must not be more than ${numberFormat(line.rate)}`}
													</div>
												)}
											</td>
											<td style={{ minWidth: '100px' }}>
												<input
													type="number"
													min={0}
													max={100}
													step="0.01"
													className={`form-control form-control-sm text-end ${isValidVat(line) ? '' : 'is-invalid'}`}
													value={line.vatPercent}
													onChange={(event) => handleLineChange(index, 'vatPercent', event.target.value)}
												/>
												{!isValidVat(line) && <div className="invalid-feedback">Enter 0 to 100</div>}
											</td>
											<td className="text-end">{vat === null ? '' : numberFormat(vat)}</td>
											<td className="text-end">{invoice1 === null ? '' : numberFormat(invoice1)}</td>
											<td className="text-end">{amount === null || rate === null ? '' : numberFormat(line.rate - rate)}</td>
											<td className="text-end">{invoice2 === null ? '' : numberFormat(invoice2)}</td>
										</tr>
									)
								})}
								{lines.length === 0 && (
									<tr>
										<td colSpan={15} className="text-center text-muted">No products found on this invoice.</td>
									</tr>
								)}
							</tbody>
							{lines.length > 0 && (
								<tfoot>
									<tr>
										<th colSpan={6} className="text-end">Total</th>
										<th className="text-end">{numberFormat(totalAmount)}</th>
										<th colSpan={4}></th>
										<th className="text-end">{numberFormat(vatTotal)}</th>
										<th className="text-end">{numberFormat(invoice1Total)}</th>
										<th></th>
										<th className="text-end">{canPrint ? numberFormat(invoice2Total) : ''}</th>
									</tr>
								</tfoot>
							)}
						</Table>

						<div className="d-flex justify-content-end mt-4">
							<button type="button" className="btn btn-sm btn-success me-2" onClick={handleSave} disabled={!canPrint || saving}>
								Save
							</button>
							<button type="button" className="btn btn-sm btn-primary me-2" onClick={() => handlePrint('entered')} disabled={!canPrint || saving}>
								Print Invoice 1 (Inter BU Rate)
							</button>
							<button type="button" className="btn btn-sm btn-primary" onClick={() => handlePrint('remaining')} disabled={!canPrint || saving}>
								Print Invoice 2 (Remaining Rate)
							</button>
						</div>
					</>
				)}
			</div>
		</div>
	)
}

export default SalesInvoiceInterBu
